import { BLOCKS } from '@contentful/rich-text-types';
/**
 * Helper to extract an item count from the title (e.g. "6-Pack")
 * Since `ProductCardWithQuickAdd` requires item count and the actual value is not saved to Shopify, we are temporarily parsing the count from the product title until `itemCount` can be decoupled from the component.
 */
var extractItemCount = function (title) {
    var match = title.match(/(\d+)\s*[-]?pack/i);
    return match && match[1] ? parseInt(match[1], 10) : 1;
};
var cleanImageUrl = function (src) { return src.replace(/^https?:/, ''); };
/**
 *  Get the price object for a product variant
 */
var getVariantPrice = function (priceStr, compareStr) {
    var price = parseFloat(priceStr.toString());
    var compare = compareStr != null ? parseFloat(compareStr.toString()) : price;
    var hasDiscount = compare > price;
    var percentOff = hasDiscount ? Math.round(100 * (1 - price / compare)) : 0;
    return {
        /**  Rebuy doesn't provide currency info, hardcoding USD */
        currency: 'USD',
        basePrice: String(hasDiscount ? compare : price),
        discountPrice: String(price),
        percentOff: percentOff
    };
};
/**
 * Transform Color Options
 */
var transformColors = function (options) {
    var colorOption = options.find(function (opt) { return opt.name && opt.name.toLowerCase() === 'color'; });
    return (((colorOption === null || colorOption === void 0 ? void 0 : colorOption.values) || []).map(function (value) { return ({
        id: value,
        title: value,
        swatch: null
    }); }) || []);
};
/**
 * Transform Size Options
 */
var transformSizes = function (options) {
    var sizeOption = options.find(function (opt) { return opt.name && opt.name.toLowerCase() === 'size'; });
    if (!sizeOption)
        return [];
    return [
        {
            id: 'size',
            title: 'Size',
            options: sizeOption.values.map(function (val) { return ({
                id: val.toLowerCase(),
                value: val.toUpperCase()
            }); })
        }
    ];
};
var transformVariantImages = function (images, productTitle) {
    return images.map(function (img) {
        var filename = img.src.split('/').pop() || '';
        return {
            id: img.id.toString(),
            title: "".concat(productTitle),
            image: {
                description: '',
                file: {
                    contentType: 'image/png',
                    details: {
                        size: 0, // Not provided by Rebuy
                        image: {
                            height: img.height,
                            width: img.width
                        }
                    },
                    fileName: filename,
                    url: cleanImageUrl(img.src)
                },
                id: img.id.toString(),
                title: "".concat(productTitle, " - image ").concat(img.id)
            }
        };
    });
};
/**
 * Transform a single Rebuy variant
 */
var transformVariant = function (variant, product) {
    var _a;
    var priceObj = getVariantPrice(variant.price, variant.compare_at_price);
    var variantImages = transformVariantImages(product.images || [], product.title);
    var colorObj = variant.option1
        ? {
            id: variant.option1,
            title: variant.option1,
            swatch: null
        }
        : null;
    var sizeArr = variant.option2
        ? [
            {
                id: variant.option2.toLowerCase(),
                title: 'size',
                value: variant.option2.toUpperCase()
            }
        ]
        : [];
    return {
        price: priceObj,
        images: variantImages,
        available: variant.inventory_quantity > 0,
        categories: product.product_type ? [product.product_type] : [],
        color: colorObj,
        externalSystems: {
            shopify: (_a = variant.id) === null || _a === void 0 ? void 0 : _a.toString()
        },
        sizes: sizeArr,
        demographics: [],
        components: [],
        itemCount: 0,
        sku: ''
    };
};
export var transformRebuyProductsToProductCards = function (rebuyProducts) {
    return rebuyProducts.map(function (product) {
        var _a, _b;
        var itemCount = extractItemCount(product.title);
        var productColors = transformColors((product.options || []));
        var productSizes = transformSizes((product.options || []));
        var variants = (_a = (product.variants || [])) === null || _a === void 0 ? void 0 : _a.map(function (variant) {
            return transformVariant(variant, product);
        });
        var featuredImage = {};
        if (product.image) {
            var fileName = product.image.src.split('/').pop() || '';
            featuredImage = {
                id: product.image.id.toString(),
                title: "".concat(product.title),
                description: '',
                file: {
                    contentType: 'image/png',
                    details: {
                        size: 0,
                        image: {
                            height: product.image.height,
                            width: product.image.width
                        }
                    },
                    fileName: fileName,
                    url: cleanImageUrl(product.image.src)
                }
            };
        }
        return {
            __typename: 'Product',
            id: product.id.toString(),
            title: product.title,
            url: "/products/".concat(product.handle),
            itemCount: itemCount,
            colors: productColors,
            externalSystems: {
                shopify: (_b = product.id) === null || _b === void 0 ? void 0 : _b.toString()
            },
            sizes: productSizes,
            variants: variants,
            featuredImage: featuredImage
        };
    });
};
export function transformMetadataToContent(metadata) {
    var _a, _b, _c, _d, _e, _f;
    var superTitle = (_c = (_b = (_a = metadata === null || metadata === void 0 ? void 0 : metadata.widget) === null || _a === void 0 ? void 0 : _a.language) === null || _b === void 0 ? void 0 : _b.super_title) !== null && _c !== void 0 ? _c : '';
    var description = (_f = (_e = (_d = metadata === null || metadata === void 0 ? void 0 : metadata.widget) === null || _d === void 0 ? void 0 : _d.language) === null || _e === void 0 ? void 0 : _e.description) !== null && _f !== void 0 ? _f : '';
    return {
        nodeType: BLOCKS.DOCUMENT,
        data: {},
        content: [
            {
                nodeType: BLOCKS.HEADING_2,
                data: {},
                content: [
                    {
                        nodeType: 'text',
                        value: superTitle,
                        marks: [],
                        data: {}
                    }
                ]
            },
            {
                nodeType: BLOCKS.PARAGRAPH,
                data: {},
                content: [
                    {
                        nodeType: 'text',
                        value: description,
                        marks: [],
                        data: {}
                    }
                ]
            }
        ]
    };
}
var transformRebuyToContentSystem = function (rebuyData) {
    var _a, _b, _c, _d;
    var cardSectionUpsellConfig = {
        eyebrow: (_d = (_c = (_b = (_a = rebuyData.metadata) === null || _a === void 0 ? void 0 : _a.widget) === null || _b === void 0 ? void 0 : _b.language) === null || _c === void 0 ? void 0 : _c.title) !== null && _d !== void 0 ? _d : '',
        cards: transformRebuyProductsToProductCards(rebuyData.data),
        content: transformMetadataToContent(rebuyData.metadata)
    };
    return cardSectionUpsellConfig;
};
export default transformRebuyToContentSystem;
